// src/pages/ForYou.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import ReelCard from "../components/ReelCard.jsx";
import TemplateCard from "../components/TemplateCard.jsx";
import ReelCardForYou from "../components/ReelCardForYou.jsx";
import TemplateCardForYou from "../components/TemplateCardForYou.jsx";
import TemplateConfirmationModal from "../components/TemplateConfirmationModal.jsx";
import ForYouPreloaderModal from "../components/ForYouPreloaderModal.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useDraftPrompt } from "../context/DraftPromptContext.jsx";
import API from "../config/api.js";

// ─── Caption Rewriter helpers ────────────────────────────────────────────────

function getCacheStorageKey(iduser) {
  return `caption_rewriter_cache:${iduser}`;
}

function loadCaptionCache(iduser) {
  try {
    const raw = localStorage.getItem(getCacheStorageKey(iduser));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCaptionCache(iduser, cache) {
  try {
    localStorage.setItem(getCacheStorageKey(iduser), JSON.stringify(cache));
  } catch {}
}

async function fetchRewrittenCaption(caption) {
  const fd = new FormData();
  fd.append("caption", caption);
  const res = await fetch(`${API.assistant}?customizeCaption`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const raw = await res.text();
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : raw;
  } catch {
    return raw;
  }
}

// Compare IDs as strings to avoid type mismatch (string vs number from API)
const sameId = (a, b) =>
  a != null && b != null && String(a) === String(b);

// ─── Sub-components ──────────────────────────────────────────────────────────

function QueueSlot({
  item,
  day,
  month,
  onUseTemplate,
  playingReelId,
  onTogglePreview,
  onVideoEnded,
  onToggleQueueTemplate,
  onToggleQueueReel,
  scheduledMap,
}) {
  if (!item) {
    return (
      <div className="card-4-5">
        <div className="card-content"></div>
      </div>
    );
  }

  const isReel = Boolean(item.idreel);
  const reference = item?.idreel
    ? `reel-${item.idreel}`
    : item?.idtemplate
      ? `template-${item.idtemplate}`
      : "";

  const scheduledInfo = reference ? scheduledMap?.[reference] : null;

  return (
    <div className="card-4-5">
      <div className="card-content">
        {isReel ? (
          <ReelCardForYou
            reel={item}
            day={day}
            month={month}
            isPlaying={playingReelId === item.idreel}
            onTogglePreview={onTogglePreview}
            onVideoEnded={onVideoEnded}
            onToggleQueue={onToggleQueueReel}
            scheduledInfo={scheduledInfo}
          />
        ) : (
          <TemplateCardForYou
            template={item}
            day={day}
            month={month}
            onUse={onUseTemplate}
            onToggleQueue={onToggleQueueTemplate}
            scheduledInfo={scheduledInfo}
          />
        )}
      </div>
    </div>
  );
}

function EventCard({ event }) {
  const parseEventDate = () => {
    if (!event?.date) return null;
    const time = event.time || "00:00:00";
    const normalizedTime = time.length === 5 ? `${time}:00` : time;
    return new Date(`${event.date}T${normalizedTime}`);
  };

  const eventDate = parseEventDate();
  const now = new Date();

  let buttonLabel = "Add To Calendar";
  let buttonHref = event?.url || "#";

  if (eventDate && !Number.isNaN(eventDate.getTime())) {
    const oneHourBefore = new Date(eventDate.getTime() - 60 * 60 * 1000);
    const oneHourAfter = new Date(eventDate.getTime() + 60 * 60 * 1000);

    if (now >= oneHourBefore && now <= oneHourAfter) {
      buttonLabel = "Join Now";
      buttonHref = event?.description || "#";
    } else if (now > oneHourAfter) {
      buttonLabel = "Watch Replay";
      buttonHref = event?.description || event?.url || "#";
    }
  }

  const formatted =
    eventDate && !Number.isNaN(eventDate.getTime())
      ? eventDate.toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "";

return (
    <div className="col-12 col-md">
      <div className="rounded-4 p-4 bg-secondary-light h-100">
        <div className="p-2">
          <h6>{event?.title || ""}</h6>
          <div className="mb-3">{formatted ? `${formatted} (ET)` : ""}</div>
          <a 
            className="btn btn-primary"
            href={buttonHref}
            target="_blank"
            rel="noreferrer"
          >
            {buttonLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

function SuggestedGrid({
  reels = [],
  templates = [],
  onUseTemplate,
  playingReelId,
  onTogglePreview,
  onVideoEnded,
}) {
  const slots = [
    {
      reel: reels[0] ?? null,
      template: templates[2] ?? null,
      reverse: false,
      desktopOnly: false,
    },
    {
      reel: reels[1] ?? null,
      template: templates[3] ?? null,
      reverse: true,
      desktopOnly: false,
    },
    {
      reel: reels[2] ?? null,
      template: templates[4] ?? null,
      reverse: false,
      desktopOnly: false,
    },
    {
      reel: reels[3] ?? null,
      template: templates[5] ?? null,
      reverse: true,
      desktopOnly: false,
    },
    {
      reel: reels[4] ?? null,
      template: templates[6] ?? null,
      reverse: false,
      desktopOnly: true,
    },
  ];

  return (
    <div className="row g-3">
      {slots.map((slot, index) => (
        <div
          key={index}
          className={`col-6 col-md${slot.desktopOnly ? " d-none d-md-block" : ""}`}
        >
          {slot.reverse ? (
            <>
              <div className="card-4-5 mb-3">
                <div className="card-content">
                  {slot.template ? (
                    <TemplateCard
                      template={slot.template}
                      onUse={onUseTemplate}
                    />
                  ) : null}
                </div>
              </div>
              <div className="card-9-16">
                <div className="card-content">
                  {slot.reel ? (
                    <ReelCard
                      reel={slot.reel}
                      isPlaying={playingReelId === slot.reel.idreel}
                      onTogglePreview={onTogglePreview}
                      onVideoEnded={onVideoEnded}
                    />
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="card-9-16 mb-3">
                <div className="card-content">
                  {slot.reel ? (
                    <ReelCard
                      reel={slot.reel}
                      isPlaying={playingReelId === slot.reel.idreel}
                      onTogglePreview={onTogglePreview}
                      onVideoEnded={onVideoEnded}
                    />
                  ) : null}
                </div>
              </div>
              <div className="card-4-5">
                <div className="card-content">
                  {slot.template ? (
                    <TemplateCard
                      template={slot.template}
                      onUse={onUseTemplate}
                    />
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ForYou() {
  const { user, manager } = useAuth();
  const { patch, openDraftPrompt, clear } = useDraftPrompt();
  const navigate = useNavigate();
  useEffect(() => {
    if (user && String(user.limitedexperience) === "1") {
      navigate("/stories", { replace: true });
    }
  }, [user, navigate]);



  const [calendar, setCalendar] = useState({});
  const [events, setEvents] = useState([]);
  const [reels, setReels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [playingReelId, setPlayingReelId] = useState(null);
  const [scheduledMap, setScheduledMap] = useState({});

  const rewriterAbortRef = useRef(false);

  // ── Caption update helper (updates reels, templates AND calendar) ──────────

  const updateCaptionEverywhere = (type, id, newCaption) => {
    if (type === "reel") {
      setReels((prev) =>
        prev.map((r) =>
          sameId(r.idreel, id) ? { ...r, caption: newCaption } : r,
        ),
      );
    } else {
      setTemplates((prev) =>
        prev.map((t) =>
          sameId(t.idtemplate, id) ? { ...t, caption: newCaption } : t,
        ),
      );
    }

    // Also update the calendar so "Your Queue" reflects the new caption
    setCalendar((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const day of Object.keys(next)) {
        const entry = next[day];
        if (!entry?.item) continue;
        const matchesReel =
          type === "reel" && sameId(entry.item.idreel, id);
        const matchesTemplate =
          type === "template" && sameId(entry.item.idtemplate, id);
        if (matchesReel || matchesTemplate) {
          next[day] = {
            ...entry,
            item: { ...entry.item, caption: newCaption },
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  // ── Caption rewriter ───────────────────────────────────────────────────────

  const runCaptionRewriter = async (
    fetchedReels,
    fetchedTemplates,
    fetchedCalendar,
  ) => {
    const iduser = user?.iduser;
    if (!iduser) return;

    rewriterAbortRef.current = false;

    const cache = loadCaptionCache(iduser);

    // Apply cached captions immediately — no API call needed
    const applyCacheToList = (items, type, idKey) =>
      items.map((item) => {
        const cached = cache[`${type}_${item[idKey]}`];
        return cached ? { ...item, caption: cached } : item;
      });

    setReels((prev) => applyCacheToList(prev, "reel", "idreel"));
    setTemplates((prev) => applyCacheToList(prev, "template", "idtemplate"));

    // Also apply cached captions to calendar (Your Queue)
    setCalendar((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const day of Object.keys(next)) {
        const entry = next[day];
        if (!entry?.item) continue;
        const key = entry.item.idreel
          ? `reel_${entry.item.idreel}`
          : entry.item.idtemplate
            ? `template_${entry.item.idtemplate}`
            : null;
        if (key && cache[key]) {
          next[day] = {
            ...entry,
            item: { ...entry.item, caption: cache[key] },
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // ── Build prioritized queue ──────────────────────────────────────────────
    // Priority 1: items visible in "Your Queue" (calendar)
    // Priority 2: remaining reels/templates from "Suggested For You"
    // Deduplicated by `${type}_${id}` so the same item isn't fetched twice.

    const seen = new Set();
    const queue = [];

    const enqueue = (type, id, caption) => {
      if (!id) return;
      const key = `${type}_${id}`;
      if (seen.has(key)) return;
      if (!caption || !caption.trim()) return;
      if (cache[key]) return; // already rewritten
      seen.add(key);
      queue.push({ type, id, caption });
    };

    // Priority 1: items currently in Your Queue (calendar)
    if (fetchedCalendar && typeof fetchedCalendar === "object") {
      for (const day of Object.keys(fetchedCalendar)) {
        const item = fetchedCalendar[day]?.item;
        if (!item) continue;
        const caption = item.caption || item.title || "";
        if (item.idreel) {
          enqueue("reel", item.idreel, caption);
        } else if (item.idtemplate) {
          enqueue("template", item.idtemplate, caption);
        }
      }
    }

    // Priority 2: rest of the reels and templates from Suggested For You
    for (const r of fetchedReels) {
      enqueue("reel", r.idreel, r.caption || r.title || "");
    }
    for (const t of fetchedTemplates) {
      enqueue("template", t.idtemplate, t.caption || t.title || "");
    }

    // ── Process queue ────────────────────────────────────────────────────────

    for (let i = 0; i < queue.length; i++) {
      if (rewriterAbortRef.current) break;

      const item = queue[i];
      const key = `${item.type}_${item.id}`;

      try {
        const newCaption = await fetchRewrittenCaption(item.caption);

        cache[key] = newCaption;
        saveCaptionCache(iduser, cache);

        updateCaptionEverywhere(item.type, item.id, newCaption);
      } catch (err) {
        console.error(`[CaptionRewriter] ${key}:`, err);
      }

      // 5s delay before next, skipped on last item
      if (i < queue.length - 1 && !rewriterAbortRef.current) {
        await new Promise((resolve) => {
          const t = setTimeout(resolve, 5000);
          const check = setInterval(() => {
            if (rewriterAbortRef.current) {
              clearTimeout(t);
              clearInterval(check);
              resolve();
            }
          }, 100);
          setTimeout(() => clearInterval(check), 5200);
        });
      }
    }
  };

  // ── Data fetching ──────────────────────────────────────────────────────────

  const formatScheduledDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const fetchForYou = async () => {
    try {
      setLoading(true);

      // Stop any in-progress rewriter before re-fetching
      rewriterAbortRef.current = true;

      const res = await fetch(`${API.forYou}?list`, { credentials: "include" });
      const data = await res.json();

      const fetchedCalendar =
        data?.calendar && typeof data.calendar === "object"
          ? data.calendar
          : {};
      const fetchedEvents = Array.isArray(data?.events) ? data.events : [];
      const fetchedReels = Array.isArray(data?.reels) ? data.reels : [];
      const fetchedTemplates = Array.isArray(data?.templates)
        ? data.templates
        : [];

      setCalendar(fetchedCalendar);
      setEvents(fetchedEvents);
      setReels(fetchedReels);
      setTemplates(fetchedTemplates);

      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();

      const calendarRes = await fetch(
        `${API.myCalendar}?list=true&month=${month}&year=${year}`,
        { credentials: "include" },
      );

      const postedDrafts = await calendarRes.json();
      const nextScheduledMap = {};

      if (Array.isArray(postedDrafts)) {
        for (const draft of postedDrafts) {
          if (!draft?.reference) continue;
          const formattedDate = formatScheduledDate(draft.date);
          nextScheduledMap[draft.reference] = {
            iddraft: draft.iddraft || "",
            scheduledText: formattedDate
              ? `Scheduled for ${formattedDate}`
              : "",
          };
        }
      }

      setScheduledMap(nextScheduledMap);

      // Kick off background caption rewriting after data is set
      runCaptionRewriter(fetchedReels, fetchedTemplates, fetchedCalendar);
    } catch (err) {
      console.error("Error For You:", err);
      setCalendar({});
      setEvents([]);
      setReels([]);
      setTemplates([]);
      setScheduledMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForYou();
    return () => {
      // Stop rewriter on unmount
      rewriterAbortRef.current = true;
    };
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────

  const startDay = useMemo(() => {
    const today = new Date().getDate();
    return today >= 26 ? 26 : today;
  }, []);

  const monthLabel = useMemo(() => {
    return new Date().toLocaleString("en-US", { month: "short" });
  }, []);

  const queueItems = useMemo(() => {
    const endDay = Math.min(startDay + 3, 30);
    const visibleCount = endDay - startDay + 1;

    return Array.from({ length: visibleCount }, (_, index) => {
      const day = startDay + index;
      const entry = calendar?.[day];
      return {
        day,
        month: monthLabel,
        item: entry?.item ?? null,
        type: entry?.type ?? null,
      };
    });
  }, [calendar, startDay, monthLabel]);

  // ── Scheduling helpers ─────────────────────────────────────────────────────

  const fallbackDateTime = () => new Date().toISOString().slice(0, 16);

  const checkNextSpotDate = async () => {
    try {
      const res = await fetch(`${API.drafts}?nextSpotDate=true`, {
        credentials: "include",
      });
      const data = await res.json();
      return data?.userdatetime || fallbackDateTime();
    } catch {
      return fallbackDateTime();
    }
  };

  const unpostDraft = async (iddraft) => {
    const formData = new FormData();
    formData.append("iddraft", iddraft);
    const res = await fetch(`${API.drafts}?unpostDraft=true`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    return res.json();
  };

  const scheduleTemplate = async (template) => {
    const userdatetime = await checkNextSpotDate();

    let ids = [];
    if (Array.isArray(template?.idimages)) {
      ids = template.idimages;
    } else if (
      typeof template?.idimages === "string" &&
      template.idimages.trim()
    ) {
      ids = template.idimages
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }

    const items = ids.map((idimage) => ({
      type: "image",
      id: idimage,
      url: `upload/image/${idimage}/image/original.jpg`,
    }));

    const formData = new FormData();
    formData.append("iddraft", "");
    formData.append("type", "images");
    formData.append("idcaption", "");
    formData.append("content", template?.caption || "");
    formData.append("facebook", "true");
    formData.append("instagram", "true");
    formData.append("linkedin", "true");
    formData.append("userdatetime", userdatetime);
    formData.append("scheduled", "true");
    formData.append("posted", "true");
    formData.append("reference", `template-${template.idtemplate}`);

    items.forEach((item, index) => {
      formData.append(`items[${index}][type]`, item.type);
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][url]`, item.url);
    });

    const res = await fetch(`${API.drafts}?add=true&returnDetails`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    return res.json();
  };

  const scheduleReel = async (reel) => {
    const userdatetime = await checkNextSpotDate();
    const reelUrl = reel?.filewithmusic || reel?.file || "";

    const formData = new FormData();
    formData.append("iddraft", "");
    formData.append("type", "reel");
    formData.append("idcaption", "");
    formData.append("content", reel?.caption || "");
    formData.append("facebook", "true");
    formData.append("instagram", "true");
    formData.append("linkedin", "true");
    formData.append("userdatetime", userdatetime);
    formData.append("scheduled", "true");
    formData.append("posted", "true");
    formData.append("reference", `reel-${reel.idreel}`);
    formData.append("items[0][type]", "reel");
    formData.append("items[0][id]", reel.idreel);
    formData.append("items[0][url]", reelUrl);

    const res = await fetch(`${API.drafts}?add=true&returnDetails`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    return res.json();
  };

  // ── Draft helpers ──────────────────────────────────────────────────────────

  const applyTemplateToDraft = (template) => {
    if (!template) return;

    clear();

    let ids = [];
    if (Array.isArray(template.idimages)) {
      ids = template.idimages;
    } else if (
      typeof template.idimages === "string" &&
      template.idimages.trim()
    ) {
      ids = template.idimages
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }

    ids.forEach((id) => {
      window._draft_prompt_item_add?.(
        "image",
        id,
        `${API.baseUrl}/upload/image/${id}/image/original.jpg`,
      );
    });

    patch({ content: template.caption || "" });

    openDraftPrompt();
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleUseTemplate = (template) => {
    if (!template) return;
    if (template.type === "images") {
      applyTemplateToDraft(template);
      return;
    }
    setSelectedTemplate(template);
  };

  const handleCloseConfirmation = () => setSelectedTemplate(null);
  const handleContinueConfirmation = () => {
    applyTemplateToDraft(selectedTemplate);
    setSelectedTemplate(null);
  };

  const handleTogglePreview = (idreel) =>
    setPlayingReelId((prev) => (prev === idreel ? null : idreel));

  const handleVideoEnded = () => setPlayingReelId(null);

  const handleToggleQueueTemplate = async ({ template, checked, iddraft }) => {
    try {
      if (checked) {
        if (!iddraft) {
          const result = await scheduleTemplate(template);
          const formattedDate = formatScheduledDate(result?.date);

          window._alert?.(
            "success",
            `✅ Post scheduled${formattedDate ? ` for ${formattedDate}` : ""}`,
          );

          setScheduledMap((prev) => ({
            ...prev,
            [`template-${template.idtemplate}`]: {
              iddraft: result?.iddraft || "",
              scheduledText: formattedDate
                ? `Scheduled for ${formattedDate}`
                : "",
            },
          }));

          return {
            success: true,
            iddraft: result?.iddraft || "",
            scheduledText: formattedDate
              ? `Scheduled for ${formattedDate}`
              : "",
          };
        }

        return {
          success: true,
          iddraft,
          scheduledText:
            scheduledMap?.[`template-${template.idtemplate}`]?.scheduledText ||
            "",
        };
      }

      if (iddraft) await unpostDraft(iddraft);

      window._alert?.("success", "🗑 Post unscheduled");

      setScheduledMap((prev) => {
        const next = { ...prev };
        delete next[`template-${template.idtemplate}`];
        return next;
      });

      return { success: true, iddraft: "", scheduledText: "" };
    } catch (error) {
      console.error("Error", error);
      window._alert?.("error", "Unable to update queue. Check if you have an active subscription.");
      return { success: false };
    }
  };

  const handleToggleQueueReel = async ({ reel, checked, iddraft }) => {
    try {
      if (checked) {
        if (!iddraft) {
          const result = await scheduleReel(reel);
          const formattedDate = formatScheduledDate(result?.date);

          window._alert?.(
            "success",
            `✅ Post scheduled${formattedDate ? ` for ${formattedDate}` : ""}`,
          );

          setScheduledMap((prev) => ({
            ...prev,
            [`reel-${reel.idreel}`]: {
              iddraft: result?.iddraft || "",
              scheduledText: formattedDate
                ? `Scheduled for ${formattedDate}`
                : "",
            },
          }));

          return {
            success: true,
            iddraft: result?.iddraft || "",
            scheduledText: formattedDate
              ? `Scheduled for ${formattedDate}`
              : "",
          };
        }

        return {
          success: true,
          iddraft,
          scheduledText:
            scheduledMap?.[`reel-${reel.idreel}`]?.scheduledText || "",
        };
      }

      if (iddraft) await unpostDraft(iddraft);

      window._alert?.("success", "🗑 Post unscheduled");

      setScheduledMap((prev) => {
        const next = { ...prev };
        delete next[`reel-${reel.idreel}`];
        return next;
      });

      return { success: true, iddraft: "", scheduledText: "" };
    } catch (error) {
      console.error("Error", error);
      window._alert?.("error", "Unable to update queue. Check if you have an active subscription.");
      return { success: false };
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AppLayout currentPage="for-you">
      <h5>Your Queue</h5>

      <div className="row g-2 mb-4">
        <div className="col-auto">
          <p className="mb-0">
            Click ✓ to approve, + to edit. Estelle AI will schedule it
            automatically at your preferred times.
          </p>
        </div>
        <div className="col text-end hide-if-app-store">
          <Link to="/social-media" className="link-primary float-end fw-bold">
            <i className="fi fi-rr-calendar"></i> View My Calendar
          </Link>
        </div>
      </div>

      <div className="suggested-for-you week-content mb-5">
        <div className="row align-items-end g-3">
          {queueItems.map((entry, index) => (
            <div
              key={
                entry.item?.idreel ||
                entry.item?.idtemplate ||
                entry.item?.idimage ||
                index
              }
              className="col-6 col-md"
            >
              <QueueSlot
                item={entry.item}
                day={entry.day}
                month={entry.month}
                onUseTemplate={handleUseTemplate}
                playingReelId={playingReelId}
                onTogglePreview={handleTogglePreview}
                onVideoEnded={handleVideoEnded}
                onToggleQueueTemplate={handleToggleQueueTemplate}
                onToggleQueueReel={handleToggleQueueReel}
                scheduledMap={scheduledMap}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="row g-3 justify-content-stretch mb-5 hide-if-app-store">
        {events.slice(0, 3).map((event, index) => (
          <EventCard key={event?.idevent || index} event={event} />
        ))}
      </div>

      <div className="suggested-for-you mb-5">
        <h5 className="mb-3">Suggested For You</h5>
        <SuggestedGrid
          reels={reels}
          templates={templates}
          onUseTemplate={handleUseTemplate}
          playingReelId={playingReelId}
          onTogglePreview={handleTogglePreview}
          onVideoEnded={handleVideoEnded}
        />
      </div>

      {loading && (
        <div className="text-center py-4">
          <div
            className="spinner-border"
            role="status"
            aria-hidden="true"
          ></div>
        </div>
      )}

      <TemplateConfirmationModal
        isOpen={Boolean(selectedTemplate)}
        editUrl={selectedTemplate?.url || ""}
        onClose={handleCloseConfirmation}
        onContinue={handleContinueConfirmation}
      />

      <ForYouPreloaderModal iduser={user?.iduser} canSkip={Boolean(manager)} />
    </AppLayout>
  );
}